import { useCallback, useEffect, useState } from "react";

/** The owner's theme setting; `system` follows the machine's preference. */
export type Theme = "system" | "light" | "dark";
/** What is actually on screen. `system` is a source, never an appearance. */
export type Resolved = "light" | "dark";

const KEY = "mosaic.theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * The stored theme setting, or `system` when none is stored or storage is unreadable.
 *
 * @internal
 * @returns The stored setting, defaulting to `system`.
 */
function read(): Theme {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    // A private window or blocked site data throws on access; the default is correct then
    // and the app must still render.
    return "system";
  }
}

/**
 * `matchMedia` is absent in jsdom and can throw behind a strict privacy setting. An
 * unanswerable query resolves to dark, the stylesheet's own default, so the fallback
 * matches what renders.
 *
 * @internal
 */
function prefersDark(): boolean {
  try {
    if (typeof window.matchMedia !== "function") return true;
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return true;
  }
}

/** The appearance a setting produces right now. */
export function resolveTheme(theme: Theme): Resolved {
  if (theme === "light" || theme === "dark") return theme;
  return prefersDark() ? "dark" : "light";
}

/**
 * Put a theme setting on the document root, where the stylesheet reads it.
 *
 * @internal
 * @param theme The setting to apply; `system` leaves the choice to the stylesheet.
 */
function apply(theme: Theme): void {
  const root = document.documentElement;
  // "system" removes the attribute entirely rather than setting it to a third value,
  // because the stylesheet's dark default plus a prefers-color-scheme override is
  // what implements it — see mosaic.css.
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

/**
 * Light and dark, and nothing in between.
 *
 * `system` is a source of a value, not an appearance, so it is never a step in the
 * control: from `system` on a dark-preferring machine, a press to `dark` would change
 * nothing on screen and look broken.
 *
 * `system` is the initial state: nobody has chosen yet, so follow the machine. `toggle`
 * resolves it to what is on screen and sets the opposite, so every press is a visible
 * two-state flip. Nothing returns to `system`.
 */
export function useTheme(): { theme: Theme; resolved: Resolved; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(read);
  const [systemDark, setSystemDark] = useState<boolean>(prefersDark);

  useEffect(() => {
    apply(theme);
    try {
      if (theme === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, theme);
    } catch {
      // Persisting is a convenience; failing to do so must not break the toggle.
    }
  }, [theme]);

  /**
   * While the setting is `system` the appearance can change without a press, and React does
   * not re-render for that, so without this the control's label describes the theme that was
   * on screen.
   */
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    let media: MediaQueryList;
    try {
      media = window.matchMedia(DARK_QUERY);
    } catch {
      return;
    }
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const resolved: Resolved =
    theme === "light" || theme === "dark" ? theme : systemDark ? "dark" : "light";

  const toggle = useCallback(() => {
    // Resolved from the current appearance rather than the stored setting: with `system`
    // stored, "the opposite of the setting" has no meaning.
    setTheme((current) => (resolveTheme(current) === "dark" ? "light" : "dark"));
  }, []);

  return { theme, resolved, toggle };
}
